/**
 * BESTIX v5.0 — Event Filters Component
 * Sidebar filters for event listing page
 */

import { useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { cn, formatPrice } from '@/lib/utils';

// ============================================
// Types
// ============================================

interface FilterOption {
  value: string;
  label: string;
  count?: number;
  children?: FilterOption[];
}

interface EventFiltersProps {
  categories?: FilterOption[];
  cities?: FilterOption[];
  minPrice?: number;
  maxPrice?: number;
  className?: string;
  onClose?: () => void;
  isMobile?: boolean;
}

// ============================================
// Icons
// ============================================

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

// ============================================
// Filter Section Component
// ============================================

interface FilterSectionProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

function FilterSection({ title, children, defaultOpen = true }: FilterSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-[var(--color-gray-200)] py-4">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full text-left focus-ring rounded"
      >
        <span className="font-inter text-sm font-semibold text-[var(--color-navy)]">
          {title}
        </span>
        <ChevronDownIcon
          className={cn(
            'w-4 h-4 text-[var(--color-gray-400)] transition-transform',
            isOpen && 'rotate-180'
          )}
        />
      </button>
      {isOpen && <div className="mt-3">{children}</div>}
    </div>
  );
}

// ============================================
// Checkbox Filter Component
// ============================================

interface CheckboxFilterProps {
  options: FilterOption[];
  selected: string[];
  onChange: (values: string[]) => void;
}

function CheckboxFilter({ options, selected, onChange }: CheckboxFilterProps) {
  // Single-select behavior: clicking a category replaces any existing selection
  const handleChange = (value: string, checked: boolean) => {
    if (checked) {
      // Single select: replace existing selection
      onChange([value]);
    } else {
      // Deselect
      onChange([]);
    }
  };

  const renderOption = (option: FilterOption, depth = 0) => {
    const hasChildren = option.children && option.children.length > 0;
    const isChecked = selected.includes(option.value);
    
    // Parent categories are non-selectable headers
    if (hasChildren) {
      return (
        <div key={option.value} className="mb-2">
          <div className="flex items-center gap-2 py-1">
            <span className="font-inter text-sm font-semibold text-[var(--color-navy)]">
              {option.label}
            </span>
            {option.count !== undefined && option.count > 0 && (
              <span className="font-inter text-xs text-[var(--color-gray-400)]">
                ({option.count})
              </span>
            )}
          </div>
          <div className="mt-1 space-y-1 ml-2">
            {option.children!.map((child) => renderOption(child, depth + 1))}
          </div>
        </div>
      );
    }
    
    // Child categories are selectable
    return (
      <div key={option.value}>
        <label
          className={cn(
            'flex items-center gap-3 cursor-pointer group py-0.5',
            depth > 0 && 'ml-2'
          )}
        >
          <input
            type="checkbox"
            checked={isChecked}
            onChange={(e) => handleChange(option.value, e.target.checked)}
            className={cn(
              'w-4 h-4 rounded border-2 border-[var(--color-gray-300)]',
              'text-[var(--color-gold)] focus:ring-[var(--color-gold)] focus:ring-offset-0',
              'cursor-pointer'
            )}
          />
          <span className={cn(
            'font-inter text-sm group-hover:text-[var(--color-navy)]',
            'text-[var(--color-gray-600)]'
          )}>
            {option.label}
          </span>
          {option.count !== undefined && option.count > 0 && (
            <span className="font-inter text-xs text-[var(--color-gray-400)] ml-auto">
              ({option.count})
            </span>
          )}
        </label>
      </div>
    );
  };

  return (
    <div className="space-y-2">
      {options.map((option) => renderOption(option))}
    </div>
  );
}

// ============================================
// Price Range Filter Component
// ============================================

interface PriceRangeFilterProps {
  min: number;
  max: number;
  currentMin: number;
  currentMax: number;
  onChange: (min: number, max: number) => void;
}

function PriceRangeFilter({ min, max, currentMin, currentMax, onChange }: PriceRangeFilterProps) {
  const [localMin, setLocalMin] = useState(currentMin);
  const [localMax, setLocalMax] = useState(currentMax);

  const handleMinChange = (value: number) => {
    const newMin = Math.min(value, localMax - 100);
    setLocalMin(newMin);
  };

  const handleMaxChange = (value: number) => {
    const newMax = Math.max(value, localMin + 100);
    setLocalMax(newMax);
  };

  const handleApply = () => {
    onChange(localMin, localMax);
  };

  const minPercent = ((localMin - min) / (max - min)) * 100;
  const maxPercent = ((localMax - min) / (max - min)) * 100;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="font-inter text-sm text-[var(--color-gray-700)]">{formatPrice(localMin)}</span>
        <span className="font-inter text-sm text-[var(--color-gray-400)]">-</span>
        <span className="font-inter text-sm text-[var(--color-gray-700)]">{formatPrice(localMax)}</span>
      </div>

      <div className="relative h-2 bg-[var(--color-gray-200)] rounded-full">
        <div
          className="absolute h-full bg-[var(--color-gold)] rounded-full"
          style={{ left: `${minPercent}%`, right: `${100 - maxPercent}%` }}
        />
        <input
          type="range"
          min={min}
          max={max}
          step={100}
          value={localMin}
          onChange={(e) => handleMinChange(Number(e.target.value))}
          className="absolute w-full h-2 appearance-none bg-transparent cursor-pointer pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[var(--color-navy)] [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow-md"
        />
        <input
          type="range"
          min={min}
          max={max}
          step={100}
          value={localMax}
          onChange={(e) => handleMaxChange(Number(e.target.value))}
          className="absolute w-full h-2 appearance-none bg-transparent cursor-pointer pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[var(--color-navy)] [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow-md"
        />
      </div>

      <button type="button" onClick={handleApply} className="w-full btn btn-ghost text-sm py-2">
        Apply Price Filter
      </button>
    </div>
  );
}

// ============================================
// Date Filter Component
// ============================================

interface DateFilterProps {
  selected: string;
  onChange: (value: string) => void;
}

function DateFilter({ selected, onChange }: DateFilterProps) {
  const options = [
    { value: '', label: 'Any Date' },
    { value: 'today', label: 'Today' },
    { value: 'tomorrow', label: 'Tomorrow' },
    { value: 'this-week', label: 'This Week' },
    { value: 'this-weekend', label: 'This Weekend' },
    { value: 'next-week', label: 'Next Week' },
    { value: 'this-month', label: 'This Month' },
    { value: 'next-month', label: 'Next Month' },
  ];

  return (
    <div className="space-y-1">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          className={cn(
            'w-full text-left px-3 py-2 rounded-md transition-colors font-inter text-sm',
            selected === option.value
              ? 'bg-[var(--color-navy)] text-white'
              : 'text-[var(--color-gray-700)] hover:bg-[var(--color-gray-100)]'
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

// ============================================
// Default Filter Options
// ============================================

const defaultCategories: FilterOption[] = [];

const defaultCities: FilterOption[] = [
  { value: 'new-york', label: 'New York', count: 312 },
  { value: 'los-angeles', label: 'Los Angeles', count: 256 },
  { value: 'chicago', label: 'Chicago', count: 178 },
  { value: 'miami', label: 'Miami', count: 145 },
  { value: 'las-vegas', label: 'Las Vegas', count: 134 },
  { value: 'atlanta', label: 'Atlanta', count: 98 },
];

// ============================================
// EventFilters Component
// ============================================

export function EventFilters({
  categories = defaultCategories,
  cities = defaultCities,
  minPrice = 0,
  maxPrice = 100000,
  className,
  onClose,
  isMobile = false,
}: EventFiltersProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const currentCategories = searchParams.get('category')?.split(',').filter(Boolean) ?? [];
  const currentCities = searchParams.get('city')?.split(',').filter(Boolean) ?? [];
  const currentDate = searchParams.get('date') ?? '';
  const currentMinPrice = Number(searchParams.get('minPrice')) || minPrice;
  const currentMaxPrice = Number(searchParams.get('maxPrice')) || maxPrice;

  const updateFilters = useCallback(
    (updates: Record<string, string | string[] | number | null>) => {
      const params = new URLSearchParams(searchParams.toString());

      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
          params.delete(key);
        } else if (Array.isArray(value)) {
          params.set(key, value.join(','));
        } else {
          params.set(key, String(value));
        }
      });

      params.delete('page');
      navigate(`/events?${params.toString()}`, { preventScrollReset: true });
    },
    [navigate, searchParams]
  );

  const clearAllFilters = () => {
    navigate('/events', { preventScrollReset: true });
    if (isMobile && onClose) {
      onClose();
    }
  };

  const hasActiveFilters =
    currentCategories.length > 0 ||
    currentCities.length > 0 ||
    currentDate !== '' ||
    currentMinPrice > minPrice ||
    currentMaxPrice < maxPrice;

  return (
    <aside className={cn('bg-white', isMobile && 'h-full flex flex-col', className)}>
      {isMobile && (
        <div className="flex items-center justify-between p-4 border-b border-[var(--color-gray-200)]">
          <h2 className="font-playfair text-lg font-bold text-[var(--color-navy)]">Filters</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-md text-[var(--color-gray-500)] hover:text-[var(--color-navy)] hover:bg-[var(--color-gray-100)] focus-ring"
            aria-label="Close filters"
          >
            <CloseIcon className="w-5 h-5" />
          </button>
        </div>
      )}

      <div className={cn('px-4', isMobile ? 'flex-1 overflow-y-auto' : 'sticky top-24')}>
        {!isMobile && (
          <div className="flex items-center justify-between py-4 border-b border-[var(--color-gray-200)]">
            <h2 className="font-playfair text-lg font-bold text-[var(--color-navy)]">Filters</h2>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearAllFilters}
                className="font-inter text-sm text-[var(--color-gold)] hover:underline focus-ring rounded"
              >
                Clear All
              </button>
            )}
          </div>
        )}

        <FilterSection title="Category">
          <CheckboxFilter
            options={categories}
            selected={currentCategories}
            onChange={(values) => updateFilters({ category: values })}
          />
        </FilterSection>

        <FilterSection title="Date">
          <DateFilter
            selected={currentDate}
            onChange={(value) => updateFilters({ date: value || null })}
          />
        </FilterSection>

        <FilterSection title="Price Range">
          <PriceRangeFilter
            min={minPrice}
            max={maxPrice}
            currentMin={currentMinPrice}
            currentMax={currentMaxPrice}
            onChange={(min, max) =>
              updateFilters({
                minPrice: min > minPrice ? min : null,
                maxPrice: max < maxPrice ? max : null,
              })
            }
          />
        </FilterSection>

        <FilterSection title="City" defaultOpen={false}>
          <CheckboxFilter
            options={cities}
            selected={currentCities}
            onChange={(values) => updateFilters({ city: values })}
          />
        </FilterSection>
      </div>

      {isMobile && (
        <div className="p-4 border-t border-[var(--color-gray-200)] space-y-3">
          {hasActiveFilters && (
            <button type="button" onClick={clearAllFilters} className="w-full btn btn-ghost">
              Clear All Filters
            </button>
          )}
          <button type="button" onClick={onClose} className="w-full btn btn-primary">
            Show Results
          </button>
        </div>
      )}
    </aside>
  );
}

export default EventFilters;
